/**
 * Film Metrics Transformation Utilities
 * Handles data transformation and timing metrics for film survey data
 * Note: Percentage calculations are now done by the API, we just transform and round
 */

import { parseDateFromAPI } from './dateUtils';

/**
 * Calculate percentage and round to whole number
 * @param {number} numerator - The numerator value
 * @param {number} denominator - The denominator value
 * @returns {number} - Rounded percentage (0-100)
 */
export const calculatePercentage = (numerator, denominator) => {
  if (!denominator || denominator === 0) return 0;
  if (!numerator || numerator < 0) return 0;
  
  const percentage = (numerator / denominator) * 100;
  return Math.round(percentage);
};

/**
 * Calculate days between two dates
 * @param {string|Object} startDate - Start date (survey date)
 * @param {string|Object} endDate - End date (release date)
 * @returns {number} - Days between dates (negative if start is after end)
 */
export const calculateDaysBetween = (startDate, endDate) => {
  const start = parseDateFromAPI(startDate);
  const end = parseDateFromAPI(endDate);
  
  if (!start || !end) return 0;
  
  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Calculate weeks between two dates
 * @param {string|Object} startDate - Start date (survey date)
 * @param {string|Object} endDate - End date (release date)
 * @returns {number} - Weeks between dates (rounded)
 */
export const calculateWeeksBetween = (startDate, endDate) => {
  const days = calculateDaysBetween(startDate, endDate);
  return Math.round(days / 7);
};

/**
 * Transform survey data using pre-calculated percentages from API
 * @param {Object} surveyWave - Survey wave data from API (now includes pre-calculated Pct fields)
 * @returns {Object} - Transformed metrics using API percentages
 */
export const transformSurveyWave = (surveyWave) => {
  // Handle both aggregation mode (camelCase) and detail mode (PascalCase) field names
  const {
    imdb_id,
    FilmRecencyOrder,
    title_text,
    SurveyCompleteDate,
    releasedate,
    // Support both naming conventions
    TotalResponses,
    totalResponses,
    TotalAware,
    totalAware,
    TotalInterest,
    totalInterest,
    TotalInterestOfAware,
    totalInterestOfAware,
    TotalInterestNotAware,
    totalInterestNotAware,
    TotalHeat,
    totalHeat,
    TotalWillingToPay,
    totalWillingToPay,
    TotalTheater,
    totalTheater,
    TotalPLF,
    totalPlf,
    record_count,
    recordCount,
    // Pre-calculated percentage fields from API (both formats)
    PctAware,
    pctAware,
    PctInterest,
    pctInterest,
    PctInterestOfAware,
    pctInterestOfAware,
    PctInterestNotAware,
    pctInterestNotAware,
    PctHeat,
    pctHeat,
    PctWillingToPay,
    pctWillingToPay,
    PctTheater,
    pctTheater,
    PctPLF,
    pctPlf
  } = surveyWave;

  // Calculate timing metrics
  const daysUntilRelease = calculateDaysBetween(SurveyCompleteDate, releasedate);
  const weeksUntilRelease = calculateWeeksBetween(SurveyCompleteDate, releasedate);

  // Use API-provided percentage metrics (already calculated and rounded)
  const metrics = {
    // Core identifiers
    imdbId: imdb_id,
    titleText: title_text,
    FilmRecencyOrder,
    recordCount: recordCount || record_count || 0,
    
    // Dates
    surveyCompleteDate: parseDateFromAPI(SurveyCompleteDate),
    releaseDate: parseDateFromAPI(releasedate),
    surveyCompleteDateRaw: SurveyCompleteDate,
    releaseDateRaw: releasedate,
    
    // Timing metrics
    daysUntilRelease,
    weeksUntilRelease,
    
    // Raw counts (support both naming conventions)
    totalResponses: TotalResponses || totalResponses || 0,
    totalAware: TotalAware || totalAware || 0,
    totalInterest: TotalInterest || totalInterest || 0,
    totalInterestOfAware: TotalInterestOfAware || totalInterestOfAware || 0,
    totalInterestNotAware: TotalInterestNotAware || totalInterestNotAware || 0,
    totalHeat: TotalHeat || totalHeat || 0,
    totalWillingToPay: TotalWillingToPay || totalWillingToPay || 0,
    totalTheater: TotalTheater || totalTheater || 0,
    totalPLF: TotalPLF || totalPlf || 0,
    
    // Pre-calculated percentage metrics from API (support both naming conventions, already as percentages, just round)
    pctAware: Math.round(PctAware || pctAware || 0),
    pctInterest: Math.round(PctInterest || pctInterest || 0),
    pctInterestOfAware: Math.round(PctInterestOfAware || pctInterestOfAware || 0),
    pctInterestNotAware: Math.round(PctInterestNotAware || pctInterestNotAware || 0),
    pctHeat: Math.round(PctHeat || pctHeat || 0),
    pctWillingToPay: Math.round(PctWillingToPay || pctWillingToPay || 0),
    pctTheater: Math.round(PctTheater || pctTheater || 0),
    pctPLF: Math.round(PctPLF || pctPlf || 0),
    
    // Additional computed fields
    isLatestWave: FilmRecencyOrder === 1,
    releaseStatus: daysUntilRelease > 0 ? 'upcoming' : 
                  daysUntilRelease === 0 ? 'today' : 'released',
    
    // Display helpers
    timingDisplay: daysUntilRelease > 0 ? 
      `${daysUntilRelease} days until release` :
      daysUntilRelease === 0 ? 'Releasing today' :
      `Released ${Math.abs(daysUntilRelease)} days ago`,
      
    weeksDisplay: weeksUntilRelease > 0 ? 
      `${weeksUntilRelease} weeks until release` :
      weeksUntilRelease === 0 ? 'Releasing this week' :
      `Released ${Math.abs(weeksUntilRelease)} weeks ago`
  };

  return metrics;
};

/**
 * Transform array of survey waves to calculated metrics
 * @param {Array} surveyWaves - Array of raw survey wave data
 * @returns {Array} - Array of transformed metrics
 */
export const transformSurveyWaves = (surveyWaves) => {
  if (!Array.isArray(surveyWaves)) return [];
  
  return surveyWaves
    .map(transformSurveyWave)
    .sort((a, b) => a.FilmRecencyOrder - b.FilmRecencyOrder); // Sort by recency order
};

/**
 * Get the most recent wave data
 * @param {Array} transformedWaves - Array of transformed survey waves
 * @returns {Object|null} - Most recent wave data or null
 */
export const getLatestWave = (transformedWaves) => {
  if (!Array.isArray(transformedWaves) || transformedWaves.length === 0) return null;
  
  return transformedWaves.find(wave => wave.FilmRecencyOrder === 1) || transformedWaves[0];
};

/**
 * Get trend data for chart visualization
 * @param {Array} transformedWaves - Array of transformed survey waves
 * @param {string} metric - Metric to trend ('pctAware', 'pctInterest', etc.)
 * @returns {Array} - Chart data array
 */
export const getTrendData = (transformedWaves, metric) => {
  if (!Array.isArray(transformedWaves)) return [];
  
  return transformedWaves
    .filter(wave => wave[metric] !== undefined)
    .sort((a, b) => new Date(a.surveyCompleteDate) - new Date(b.surveyCompleteDate))
    .map(wave => ({
      date: wave.surveyCompleteDate.toLocaleDateString(),
      value: wave[metric],
      weeksUntilRelease: wave.weeksUntilRelease,
      daysUntilRelease: wave.daysUntilRelease,
      FilmRecencyOrder: wave.FilmRecencyOrder,
      isLatest: wave.isLatestWave
    }));
};

/**
 * Get available filter options from survey data
 * @param {Array} surveyWaves - Raw survey wave data
 * @returns {Object} - Available filter options
 */
export const getFilterOptions = () => {
  return {
    movieFrequency: [
      { value: 'Frequents', label: 'Frequent Movie Goers' },
      { value: 'Casual', label: 'Casual Movie Goers' },
      { value: 'Home', label: 'Home Viewers' },
      { value: 'Non-Movie Watchers', label: 'Non-Movie Watchers' }
    ],
    ageGroup: [
      { value: 'Under 35', label: 'Under 35' },
      { value: 'Over 35', label: 'Over 35' }
    ],
    gender: [
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Other', label: 'Other' }
    ],
    ethnicity: [
      { value: 'White', label: 'White' },
      { value: 'Black', label: 'Black' },
      { value: 'Hispanic', label: 'Hispanic' },
      { value: 'Asian/Pacific Islander', label: 'Asian/Pacific Islander' },
      { value: 'Other', label: 'Other' }
    ]
  };
};

const filmMetricsUtils = {
  calculatePercentage,
  calculateDaysBetween,
  calculateWeeksBetween,
  transformSurveyWave,
  transformSurveyWaves,
  getLatestWave,
  getTrendData,
  getFilterOptions
};

export default filmMetricsUtils;
